import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdaptivePolling } from './useAdaptivePolling';
import { queryPrometheusInstantVector } from '../services/grafana';
import api, { DbBot } from '../services/api';
import { TELEMETRY_FRESHNESS_SEC } from '../utils/telemetryFreshness';

// A bot is considered "reporting" (has a live heartbeat) when its most recent
// telemetry sample is newer than this. Shares the single freshness window every
// bot-liveness panel uses, so a reduced fleet falls off every panel at once.
export const HEARTBEAT_FRESH_SEC = TELEMETRY_FRESHNESS_SEC;

// A bot row for the dashboard table. Inventory fields (provider/region/ip/status)
// come from the database; the heartbeat overlay (reporting/lastSeenMs) is derived
// from fresh Prometheus telemetry matched by public_ip.
export interface DashboardBotRow {
  id: string;
  provider: string;
  region: string;
  public_ip: string;
  /**
   * Public IPv6, derived from telemetry labels (matched by public_ip) since the
   * database does not store it. Undefined when the bot reports no valid IPv6.
   */
  public_ipv6?: string;
  status: string;
  /** True when the bot pushed telemetry within HEARTBEAT_FRESH_SEC. */
  reporting: boolean;
  /** Epoch ms of the bot's most recent telemetry sample, or null if never seen. */
  lastSeenMs: number | null;
}

// Telemetry label values that stand in for "no IPv6" and should not be shown.
const INVALID_IPV6 = new Set(['', 'unknown', 'undefined']);

// The most recent telemetry sample for a single public_ip, carrying the labels
// needed to render a row for a bot that is no longer in the DB inventory.
interface TelemetrySample {
  lastSeenMs: number;
  ipv6?: string;
  provider: string;
  region: string;
}

interface UseDashboardBotsOptions {
  tenantId?: string | null;
  scenarioId?: string | null;
  scenarioIds?: Array<string | null | undefined>;
  refreshInterval?: number;
  enabled?: boolean;
}

const escapeLabelValue = (value: string) =>
  value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/**
 * Sources the dashboard bot table from the database (GET /bots/all) rather than
 * from Prometheus, then overlays a per-bot heartbeat derived from telemetry
 * freshness (matched by public_ip). Rows are scoped to the currently selected
 * scenario and exclude terminated bots (the server already filters those).
 */
export function useDashboardBots({
  tenantId,
  scenarioId,
  scenarioIds,
  refreshInterval = 5000,
  enabled = true,
}: UseDashboardBotsOptions) {
  const [bots, setBots] = useState<DbBot[]>([]);
  // public_ip -> freshest telemetry sample (heartbeat + provider/region/ipv6 labels)
  const [telemetryByIp, setTelemetryByIp] = useState<Record<string, TelemetrySample>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Stable, order-independent key for the active scenario id set.
  const scenarioKey = useMemo(() => {
    const raw = (scenarioIds && scenarioIds.length ? scenarioIds : [scenarioId])
      .map((v) => (v || '').trim())
      .filter(Boolean);
    return Array.from(new Set(raw)).sort().join('|');
  }, [scenarioIds, scenarioId]);

  const intervalMs = useAdaptivePolling({
    activeMs: refreshInterval,
    idleMs: Math.max(30_000, refreshInterval),
  });

  const buildSelector = useCallback(() => {
    const parts: string[] = [`tenant_id="${escapeLabelValue(tenantId || '')}"`];
    const ids = scenarioKey ? scenarioKey.split('|') : [];
    if (ids.length > 1) {
      // Scenario ids are UUIDs; strip anything outside [A-Za-z0-9_-] so the
      // (fully-anchored) =~ alternation stays regex-safe.
      const alternation = ids
        .map((id) => id.replace(/[^a-zA-Z0-9_-]/g, ''))
        .filter(Boolean)
        .join('|');
      parts.push(`scenario_id=~"${alternation}"`);
    } else {
      parts.push(`scenario_id="${escapeLabelValue(ids[0] || '')}"`);
    }
    return `{${parts.join(',')}}`;
  }, [tenantId, scenarioKey]);

  const fetchAll = useCallback(async () => {
    if (!enabled) return;
    // No scenario selected → nothing to show (matches the previous behaviour
    // where an empty scenario selector returned no bots).
    if (!scenarioKey) {
      if (mountedRef.current) {
        setBots([]);
        setTelemetryByIp({});
      }
      return;
    }

    const selector = buildSelector();
    // Age (seconds) of the most recent telemetry sample per bot IP. Computed in
    // PromQL so it is immune to browser/Prometheus clock skew. Grouped by the
    // ipv6/provider/region labels so they ride along — the provider/region are
    // what let us render a row for a bot that has already left the DB inventory.
    const heartbeatQuery = `min by (public_ip, public_ipv6, provider, region) (time() - timestamp(network_bandwidth_down_bytes_total${selector}))`;

    try {
      const [allBots, heartbeatVector] = await Promise.all([
        api.getAllBots(),
        queryPrometheusInstantVector(heartbeatQuery).catch(() => []),
      ]);

      if (!mountedRef.current) return;

      const now = Date.now();
      const telemetry: Record<string, TelemetrySample> = {};
      for (const entry of heartbeatVector) {
        const ip = entry.metric?.public_ip;
        if (!ip) continue;
        const ageSec = Number(entry.value);
        if (!Number.isFinite(ageSec) || ageSec < 0) continue;
        const ts = now - ageSec * 1000;
        // Keep the freshest sample if an IP appears more than once.
        const existing = telemetry[ip];
        if (existing && existing.lastSeenMs >= ts) continue;
        const v6 = entry.metric?.public_ipv6 || '';
        telemetry[ip] = {
          lastSeenMs: ts,
          ipv6: INVALID_IPV6.has(v6.toLowerCase()) ? undefined : v6,
          provider: entry.metric?.provider || entry.metric?.cloud_provider || 'unknown',
          region: entry.metric?.region || entry.metric?.cloud_region || 'unknown',
        };
      }

      setBots(allBots?.bots ?? []);
      setTelemetryByIp(telemetry);
      setError(null);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err?.message ?? 'Failed to load bots');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled, scenarioKey, buildSelector]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    fetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  useEffect(() => {
    if (!enabled || !scenarioKey) return;
    const id = window.setInterval(fetchAll, intervalMs);
    return () => window.clearInterval(id);
  }, [fetchAll, intervalMs, enabled, scenarioKey]);

  const rows: DashboardBotRow[] = useMemo(() => {
    const scenarioSet = new Set(scenarioKey ? scenarioKey.split('|') : []);
    const now = Date.now();
    const isFresh = (lastSeenMs: number | null) =>
      lastSeenMs != null && now - lastSeenMs < HEARTBEAT_FRESH_SEC * 1000;

    const dbRows = bots
      .filter((b) => b.scenario_id != null && scenarioSet.has(b.scenario_id))
      .map((b) => {
        const ip = b.public_ip || '';
        const lastSeenMs = ip ? telemetryByIp[ip]?.lastSeenMs ?? null : null;
        return {
          id: b.id,
          // Use the raw provider_type / region_code so these values match the
          // Prometheus `provider` / `region` labels — the dashboard's filter bar
          // feeds them straight into the telemetry PromQL selector.
          provider: b.provider_type || 'unknown',
          region: b.region_code || b.region_name || b.region_id || 'unknown',
          public_ip: ip,
          public_ipv6: ip ? telemetryByIp[ip]?.ipv6 : undefined,
          status: b.status,
          reporting: isFresh(lastSeenMs),
          lastSeenMs,
        };
      });

    // A bot dropped from the DB inventory (e.g. the user reduced the fleet) can
    // keep pushing telemetry until its VM is fully torn down. Surface it as a
    // synthetic "terminating" row while it is still reporting, so it doesn't
    // silently vanish from the table before it has actually stopped. It drops
    // off on its own once the heartbeat goes stale.
    const dbIps = new Set(dbRows.map((r) => r.public_ip).filter(Boolean));
    const terminatingRows = Object.entries(telemetryByIp)
      .filter(([ip, s]) => ip && !dbIps.has(ip) && isFresh(s.lastSeenMs))
      .map(([ip, s]) => ({
        id: `terminating-${ip}`,
        provider: s.provider,
        region: s.region,
        public_ip: ip,
        public_ipv6: s.ipv6,
        status: 'terminating',
        reporting: true,
        lastSeenMs: s.lastSeenMs,
      }));

    return [...dbRows, ...terminatingRows];
  }, [bots, telemetryByIp, scenarioKey]);

  return { rows, loading, error };
}
